// utils/misc.js


/**
 * Copy the values of all enumerable own properties from one or more objects to a target object.
 * Portion taken from the Object.assign(target, ...sources) polyfill on MDN.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#Polyfill
 * @param {object} target Target object
 * @param {...object} sources Additional objects whose properties to copy into target
 * @return {object} target
 */
/* eslint-disable */
export function assign(target, ...sources) {
  if (target == null) { // TypeError if undefined or null
    throw new TypeError('Cannot convert undefined or null to object');
  }

  var to = target;

  for (var index = 1; index < arguments.length; index++) {
    var nextSource = arguments[index];

    if (nextSource != null) { // Skip over if undefined or null
      for (var nextKey in nextSource) {
        // Avoid bugs when hasOwnProperty is shadowed
        if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
          to[nextKey] = nextSource[nextKey];
        }
      }
    }
  }
  return to;
}

/**
 *
 * @param {(number|HTMLElement)} destination - Destination to scroll to (DOM element or number)
 * @param {number} duration - Duration of scrolling animation
 * @param {string} easing - Timing function name (Allowed values: 'linear', 'easeInQuad', 'easeOutQuad', 'easeInOutQuad', 'easeInCubic', 'easeOutCubic', 'easeInOutCubic', 'easeInQuart', 'easeOutQuart', 'easeInOutQuart', 'easeInQuint', 'easeOutQuint', 'easeInOutQuint')
 * @param {(number|HTMLElement)} offset - Optional element (such as a fixed header, etc.) that needs to be offset to calculate the correct point to scroll can also be a number if you want to specify something specific, ex: 300
 * @param {function} callback - Optional callback invoked after animation
 */
export function scrollIt(destination, duration = 200, easing = 'linear', offset, callback) {
  // Predefine list of available timing functions
  // If you need more, tween js is full of great examples
  // https://github.com/tweenjs/tween.js/blob/master/src/Tween.js#L421-L737
  const easings = {
    linear(t) {
      return t;
    },
    easeInQuad(t) {
      return t * t;
    },
    easeOutQuad(t) {
      return t * (2 - t);
    },
    easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
    easeInCubic(t) {
      return t * t * t;
    },
    easeOutCubic(t) {
      return (--t) * t * t + 1;
    },
    easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    },
    easeInQuart(t) {
      return t * t * t * t;
    },
    easeOutQuart(t) {
      return 1 - (--t) * t * t * t;
    },
    easeInOutQuart(t) {
      return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
    },
    easeInQuint(t) {
      return t * t * t * t * t;
    },
    easeOutQuint(t) {
      return 1 + (--t) * t * t * t * t;
    },
    easeInOutQuint(t) {
      return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;
    },
  };

  // Store initial position of a window and time
  // If performance is not available in your browser
  // It will fallback to new Date().getTime() - thanks IE < 10
  const start = window.pageYOffset;
  const startTime = 'now' in window.performance ? performance.now() : new Date().getTime();
  const documentHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
  const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.getElementsByTagName('body')[0].clientHeight;
  // get height of any element that requires a little extra offset
  // if no offset element is set, default to 0
  // add some padding so it looks a little nicer on scroll
  var extraOffsetHeight;
  typeof offset === 'undefined' ? extraOffsetHeight = 0 : typeof offset === 'number' ? extraOffsetHeight = offset : extraOffsetHeight = offset.offsetHeight;
  // get top of boundingClientRect because offsetTop is relative to the parent (which is position relative)
  // add the current scroll position of the window
  // minus height of fixed header
  const destinationBound = typeof destination === 'number' ? destination : Math.round(destination.getBoundingClientRect().top + window.pageYOffset - extraOffsetHeight);

  if ('requestAnimationFrame' in window === false) {
    window.scrollTo(0, destinationBound);
    if (callback) {
      callback();
    }
    return;
  }

  function scroll() {
    const now = 'now' in window.performance ? performance.now() : new Date().getTime();
    const time = Math.min(1, ((now - startTime) / duration));
    const timeFunction = easings[easing](time);
    const scrollPosition = Math.ceil((timeFunction * (destinationBound - start)) + start);
    window.scrollTo(0, scrollPosition);

    if (window.pageYOffset === destinationBound || scrollPosition === destinationBound) {
      if (callback) {
        callback();
      }
      return;
    } else if (((window.innerHeight + window.scrollY) >= document.body.offsetHeight || (window.innerHeight + document.documentElement.scrollTop) >= document.body.offsetHeight) && (window.pageYOffset < destinationBound)) {
      // check to see if the user hits the bottom before the destination is reached, or else the scroll will get stuck on shorter pages
      if (callback) {
        callback();
      }
      return;
    }

    requestAnimationFrame(scroll);
  }

  scroll();
}

/**
 *
 * @param {number} amount - Amount to be formatted
 */
export function formatBasicCurrency(amount) {
  // add currency punctuation as needed
  const formatted = parseFloat(amount).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
  return `$${formatted}`;
}
/* eslint-enable */



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/utils/misc.js